/**
 * AvatarMenu.tsx
 *
 * User avatar with dropdown menu for account actions.
 * Positioned at the bottom of the AdminRail.
 */

import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { Settings, Shield, LogOut, User, Moon, Sun, Bug, Lightbulb, Key, Smartphone, BookOpen } from 'lucide-react';
import { useAuthStore } from '@/base/stores/authStore';
import { useThemeStore } from '@/base/stores/themeStore';

// ============================================================================
// Types
// ============================================================================

interface AvatarMenuProps {
  collapsed?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function AvatarMenu({ collapsed = false }: AvatarMenuProps) {
  const { user, signOut } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();

  // Generate initials
  const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleLogout = async () => {
    await signOut();
  };

  // If no user is authenticated, don't show user info - they should be redirected to login
  const displayUser = user || {
    name: 'Guest',
    email: '',
  };

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <PopoverButton
            className={`
              flex items-center gap-3 w-full p-3 rounded-lg
              transition-all duration-200
              hover:bg-[var(--grok-hover-bg)]
              focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ems-primary-ring)]
              ${open ? 'bg-[var(--grok-active-bg)]' : ''}
              ${collapsed ? 'justify-center' : ''}
            `}
            aria-label="User menu"
          >
            {/* Avatar */}
            <div
              className="
                w-10 h-10 rounded-full flex-shrink-0
                flex items-center justify-center
                bg-[var(--grok-surface-2)] text-[var(--grok-text-primary)]
                text-sm font-medium border border-[var(--grok-border)]
              "
            >
              {getInitials(displayUser.name)}
            </div>

            {/* User info */}
            {!collapsed && (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-[var(--grok-text-primary)] truncate">
                  {displayUser.name}
                </p>
                <p className="text-xs text-[var(--grok-text-muted)] truncate">
                  {displayUser.email}
                </p>
              </div>
            )}
          </PopoverButton>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel
              className={`
                absolute z-50 w-64 py-2
                bg-[var(--grok-surface-1)] border border-[var(--grok-border)]
                rounded-lg shadow-xl
                ${collapsed ? 'left-full ml-2 bottom-0' : 'left-0 bottom-full mb-2'}
              `}
            >
              {/* Account Header */}
              <div className="px-4 py-3 border-b border-[var(--grok-border)]">
                <div className="flex items-center gap-3">
                  <div
                    className="
                      w-10 h-10 rounded-full flex items-center justify-center
                      bg-[var(--grok-surface-2)] text-[var(--grok-text-primary)]
                      text-sm font-medium
                    "
                  >
                    {getInitials(displayUser.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--grok-text-primary)] truncate">
                      {displayUser.name}
                    </p>
                    <p className="text-xs text-[var(--grok-text-muted)] truncate">
                      {displayUser.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Support Items */}
              <div className="py-1">
                <MenuLink to="/support/bugs" icon={Bug} label="Report Bug" onClick={close} />
                <MenuLink to="/support/features" icon={Lightbulb} label="Request Feature" onClick={close} />
              </div>

              {/* Documentation */}
              <div className="border-t border-[var(--grok-border)] my-1" />
              <div className="py-1">
                <a
                  href="/docs"
                  onClick={close}
                  className="
                    flex items-center gap-3 px-4 py-2.5
                    text-[var(--grok-text-primary)] text-sm
                    transition-all duration-150
                    hover:bg-[var(--grok-hover-bg)]
                    focus:outline-none focus-visible:bg-[var(--grok-hover-bg)]
                  "
                >
                  <BookOpen size={18} className="text-[var(--grok-text-secondary)]" />
                  <span>Documentation</span>
                </a>
              </div>

              {/* Menu Items */}
              <div className="border-t border-[var(--grok-border)] my-1" />
              <div className="py-1">
                <MenuLink to="/profile" icon={User} label="Profile" onClick={close} />
                <MenuLink to="/credentials" icon={Key} label="Credential Manager" onClick={close} />
                <MenuLink to="/credentials/totp" icon={Smartphone} label="TOTP Manager" onClick={close} />
                <MenuLink to="/settings" icon={Settings} label="Settings" onClick={close} />

                {/* Theme Toggle */}
                <button
                  onClick={() => {
                    toggleTheme();
                  }}
                  className="
                    flex items-center gap-3 w-full px-4 py-2.5
                    text-[var(--grok-text-primary)] text-sm
                    transition-all duration-150
                    hover:bg-[var(--grok-hover-bg)]
                    focus:outline-none focus-visible:bg-[var(--grok-hover-bg)]
                  "
                >
                  {theme === 'dark' ? (
                    <Sun size={18} className="text-[var(--grok-text-secondary)]" />
                  ) : (
                    <Moon size={18} className="text-[var(--grok-text-secondary)]" />
                  )}
                  <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
              </div>

              {/* Admin Section */}
              <div className="border-t border-[var(--grok-border)] my-1" />
              <div className="py-1">
                <MenuLink to="/admin/audit" icon={Shield} label="Audit Log" onClick={close} />
              </div>

              {/* Logout */}
              <div className="border-t border-[var(--grok-border)] my-1" />
              <div className="py-1">
                <button
                  onClick={() => {
                    close();
                    handleLogout();
                  }}
                  className="
                    flex items-center gap-3 w-full px-4 py-2.5
                    text-[var(--ems-danger)] text-sm
                    transition-all duration-150
                    hover:bg-[var(--ems-danger-muted)]
                    focus:outline-none focus-visible:bg-[var(--ems-danger-muted)]
                  "
                >
                  <LogOut size={18} />
                  <span>Sign Out</span>
                </button>
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

// ============================================================================
// Helper Components
// ============================================================================

interface MenuLinkProps {
  to: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  onClick?: () => void;
}

function MenuLink({ to, icon: Icon, label, onClick }: MenuLinkProps) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="
        flex items-center gap-3 px-4 py-2.5
        text-[var(--grok-text-primary)] text-sm
        transition-all duration-150
        hover:bg-[var(--grok-hover-bg)]
        focus:outline-none focus-visible:bg-[var(--grok-hover-bg)]
      "
    >
      <Icon size={18} className="text-[var(--grok-text-secondary)]" />
      <span>{label}</span>
    </Link>
  );
}

export default AvatarMenu;
